import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Play, X, Clock, Repeat, Flame, Award, Heart, ChevronRight,
} from "lucide-react";

const TRAINEE = "YAĞMUR BOLAT";
const MOTTO = "3 gün, tüm vücut — kalça, üst gövde ve merkez.";

const DAYS = [
  {
    key: "day1",
    label: "1. Gün",
    focus: "Alt Vücut & Kalça",
    color: "from-rose-500/25 to-rose-500/5",
    accent: "rose",
    exercises: [
      { name: "Barbell Hip Thrust", reps: "4 × 12", video: "LM8XHLYJoYs", note: "Kalçayı sık, üstte 1 sn tut" },
      { name: "Goblet Squat", reps: "3 × 12", video: "MxsFDhcyFyE", note: "Diz uçlarını takip et" },
      { name: "Romanian Deadlift", reps: "3 × 12", video: "hCDzSR6bW10", note: "Sırt düz, kalça geri" },
      { name: "Bulgarian Split Squat", reps: "3 × 10 (her bacak)", video: "2C-uNgKwPLE", note: "Dengeye odaklan" },
      { name: "Cable Glute Kickback", reps: "3 × 12 (her bacak)", video: "5jJNfIlKTmg", note: "Kontrollü tekme" },
      { name: "Glute Bridge", reps: "3 × 15", video: "OUgsJ8-Vi0E", note: "Bitirme hareketi" },
    ],
  },
  {
    key: "day2",
    label: "2. Gün",
    focus: "Üst Vücut — Sırt · Göğüs · Omuz",
    color: "from-cyan-500/25 to-cyan-500/5",
    accent: "primary",
    exercises: [
      { name: "Lat Pulldown", reps: "4 × 12", video: "CAwf7n6Luuc", note: "Kanatları çalıştır" },
      { name: "Seated Cable Row", reps: "3 × 12", video: "GZbfZ033f74", note: "Omuz bıçaklarını sık" },
      { name: "Machine Chest Press", reps: "3 × 12", video: "xUm0BiZCWlQ", note: "Göğüs orta bölge" },
      { name: "Seated Overhead Press", reps: "3 × 10", video: "2yjwXTZQDDI", note: "Omuz açısını koru" },
      { name: "Cable Lateral Raise", reps: "3 × 12", video: "Z5FA9aq3L6A", note: "Yan omuz izolasyonu" },
      { name: "Face Pull", reps: "3 × 15", video: "rep-qVOkqgk", note: "Postür & arka omuz" },
    ],
  },
  {
    key: "day3",
    label: "3. Gün",
    focus: "Karın & Kardiyo",
    color: "from-amber-500/25 to-amber-500/5",
    accent: "amber",
    badge: "CORE + KARDİYO",
    exercises: [
      { name: "Plank", reps: "3 × 45 sn", video: "pSHjTRCQxIw", note: "Kalça çök yok" },
      { name: "Hanging Knee Raise", reps: "3 × 12", video: "Pr1ieGZ5atk", note: "Karnını kasarak" },
      { name: "Russian Twist", reps: "3 × 20 (10 her yön)", video: "wkD8rjkodUI", note: "Yan karın" },
      { name: "Dead Bug", reps: "3 × 12", video: "4XLEnwUr1d8", note: "Bel yerde kalsın" },
      { name: "Bicycle Crunch", reps: "3 × 20", video: "9FGilxCbdz8", note: "Rotasyon kontrollü" },
      { name: "Side Plank", reps: "3 × 30 sn (her taraf)", video: "K2VljzCC16g", note: "Kalça yükseği tut" },
    ],
    cardio: "20-25 dakika eğimli yürüyüş veya bisiklet",
  },
];

const TIPS = [
  { icon: "💧", text: "Günde 2-3 litre su iç." },
  { icon: "🍗", text: "Her öğünde protein kaynağı bulundur (yumurta, tavuk, yoğurt, whey)." },
  { icon: "😴", text: "7-9 saat uyu — toparlanma antrenmanla eşit derecede önemli." },
  { icon: "📈", text: "Ağırlıkları yavaş yavaş arttır. Doğru form her şeyden değerlidir." },
];

export default function YagmurPlan() {
  const [day, setDay] = useState(0);
  const [video, setVideo] = useState(null);

  const todayIdx = (() => {
    const d = new Date().getDay();
    return d % 3;
  })();

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="yagmur-page">
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="container-x flex items-center justify-between h-14">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm font-display uppercase tracking-wider">
            <ArrowLeft className="h-4 w-4" /> Anasayfa
          </Link>
          <div className="flex items-center gap-2">
            <img src="/assets/fcs-logo.png" alt="FCS" className="h-9 w-9" />
            <span className="font-display font-black uppercase tracking-wider text-sm">GoodPT</span>
          </div>
          <div className="w-12" />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-24 -right-24 h-[360px] w-[360px] rounded-full bg-rose-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-[320px] w-[320px] rounded-full bg-primary/15 blur-3xl" />

        <div className="container-x relative py-10 md:py-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/15 ring-1 ring-rose-500/30 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-rose-400 pulse-soft" />
            <span className="text-[10px] font-display font-bold uppercase tracking-[0.25em] text-rose-300">Kişisel Antrenman · 3 Gün</span>
          </div>
          <div className="text-xs font-display uppercase tracking-[0.3em] text-muted-foreground mb-2">Hazırlanan Sporcu</div>
          <h1 className="font-brand uppercase leading-[0.9]" style={{ fontSize: "clamp(2.25rem, 9vw, 4.75rem)" }}>
            {TRAINEE}
          </h1>
          <p className="mt-4 max-w-md mx-auto text-sm md:text-base text-muted-foreground">
            {MOTTO}
          </p>
        </div>
      </section>

      {/* PROGRAM PILLS */}
      <section className="py-6 border-b border-border bg-[hsl(218_55%_5%)]">
        <div className="container-x">
          <div className="grid grid-cols-4 gap-3">
            <Info label="Gün" value="3" sub="Haftada" />
            <Info label="Set × Tekrar" value="3-4 × 12" sub="Standart" />
            <Info label="Dinlenme" value="60-90 sn" sub="Setler arası" />
            <Info label="Süre" value="~45 dk" sub="Antrenman" />
          </div>
        </div>
      </section>

      {/* DAY TABS */}
      <section className="py-10" data-testid="yagmur-weekly">
        <div className="container-x max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <div className="eyebrow justify-center mb-1">Haftalık Bölünme</div>
            <h2 className="heading-md">3 Günlük Split</h2>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-6" data-testid="day-buttons">
            {DAYS.map((d, i) => {
              const active = i === day;
              const isToday = i === todayIdx;
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setDay(i)}
                  data-testid={`ytab-${i}`}
                  className={`relative rounded-md border py-3 px-2 text-center transition-all ${
                    active
                      ? "bg-primary text-primary-foreground border-primary shadow-[0_6px_18px_-6px_hsl(202_100%_50%/0.4)]"
                      : "border-border bg-card hover:border-primary"
                  }`}
                >
                  <div className="font-display font-black uppercase text-sm md:text-base tracking-wider leading-none">{d.label}</div>
                  <div className={`mt-1 text-[10px] font-display uppercase tracking-[0.14em] ${active ? "opacity-90" : "text-muted-foreground"}`}>
                    {d.focus.split("—")[0].trim().split("&")[0].trim()}
                  </div>
                  {isToday && !active && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-background pulse-soft" />
                  )}
                </button>
              );
            })}
          </div>

          <div className={`card-elev p-5 md:p-6 bg-gradient-to-br ${DAYS[day].color}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-border">
              <div>
                <div className="eyebrow mb-1">{DAYS[day].label}</div>
                <h3 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight">{DAYS[day].focus}</h3>
              </div>
              {DAYS[day].badge && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 ring-1 ring-amber-500/40 text-[10px] font-display font-black uppercase tracking-[0.2em] text-amber-300">
                  <Award className="h-3 w-3" /> {DAYS[day].badge}
                </span>
              )}
            </div>

            <div className="space-y-2.5">
              {DAYS[day].exercises.map((ex, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setVideo(ex)}
                  data-testid={`yex-${day}-${i}`}
                  className="w-full group flex items-center gap-3 p-3 rounded-md border border-border bg-card/60 hover:bg-card hover:border-primary transition-all text-left"
                >
                  <div className="font-display text-lg font-black text-primary w-7 text-center shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase tracking-wider text-sm truncate">{ex.name}</div>
                    <div className="text-xs text-muted-foreground">
                      <span className="text-primary/90 font-semibold">{ex.reps}</span>
                      {ex.note && <span className="opacity-75"> · {ex.note}</span>}
                    </div>
                  </div>
                  <div className="h-9 w-9 shrink-0 rounded-full bg-primary/15 ring-1 ring-primary/30 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Play className="h-3.5 w-3.5" fill="currentColor" />
                  </div>
                </button>
              ))}
            </div>

            {DAYS[day].cardio && (
              <div className="mt-5 p-4 rounded-md border border-amber-500/40 bg-amber-500/5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-amber-500/20 ring-1 ring-amber-500/40 flex items-center justify-center text-amber-300">
                  <Heart className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[10px] font-display uppercase tracking-[0.22em] text-amber-300">Kardiyo</div>
                  <div className="font-display font-bold text-sm">{DAYS[day].cardio}</div>
                </div>
              </div>
            )}

            <div className="mt-6 grid grid-cols-3 gap-3 pt-5 border-t border-border">
              <MiniStat icon={Repeat} label="Set × Tekrar" value="3-4 × 12" />
              <MiniStat icon={Clock} label="Dinlenme" value="60-90 sn" />
              <MiniStat icon={Flame} label="Tempo" value="Kontrollü" />
            </div>
          </div>
        </div>
      </section>

      {/* TIPS */}
      <section className="py-10 border-y border-border bg-[hsl(218_55%_5%)]">
        <div className="container-x max-w-2xl mx-auto">
          <div className="text-center mb-5">
            <div className="eyebrow justify-center">Başarı İpuçları</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {TIPS.map((t, i) => (
              <div key={i} className="card-elev p-4 flex items-start gap-3">
                <span className="text-2xl">{t.icon}</span>
                <span className="text-sm text-muted-foreground leading-relaxed">{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        <img src="/assets/fcs-logo.png" alt="FCS" className="h-10 w-10 mx-auto mb-2" />
        Focus Spor Kulübü · GoodPT — {TRAINEE}
      </footer>

      {video && <VideoModal video={video} onClose={() => setVideo(null)} />}
    </div>
  );
}

function Info({ label, value, sub }) {
  return (
    <div className="card-elev p-3 text-center">
      <div className="text-[9px] font-display uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-display text-lg md:text-xl font-black text-primary leading-none">{value}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-md bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center text-primary shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[9px] font-display uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
        <div className="font-display font-bold text-sm">{value}</div>
      </div>
    </div>
  );
}

function VideoModal({ video, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-background/95 backdrop-blur-md p-4 sm:p-8 flex items-start sm:items-center justify-center overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-3xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground">Hareket</div>
            <h3 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight">{video.name}</h3>
            <div className="text-sm text-primary mt-1">{video.reps}</div>
          </div>
          <button type="button" onClick={onClose} className="h-11 w-11 rounded-md border border-border bg-card hover:border-primary hover:text-primary">
            <X className="h-5 w-5 mx-auto" />
          </button>
        </div>

        <div className="aspect-video w-full rounded-lg overflow-hidden border border-border bg-black">
          <iframe
            title={video.name}
            src={`https://www.youtube.com/embed/${video.video}?autoplay=1&modestbranding=1&rel=0`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className="mt-4 flex flex-wrap justify-between gap-3">
          <a href={`https://www.youtube.com/watch?v=${video.video}`} target="_blank" rel="noreferrer" className="btn-secondary text-xs px-4 py-2">
            YouTube&apos;da Aç
          </a>
          <button type="button" onClick={onClose} className="btn-primary text-xs px-4 py-2">
            Anladım <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
